import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import LogoMark from "@/components/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentStatus({ success = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(sessionId && success ? "pending" : success ? "pending" : "cancel");

  useEffect(() => {
    if (!sessionId || !success) return undefined;
    let stop = false;
    const poll = async () => {
      while (!stop) {
        try {
          const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            setStatus("paid");
            return;
          }
          if (data.status === "expired" || data.status === "failed") {
            setStatus("failed");
            return;
          }
        } catch (e) {
          setStatus("failed");
          return;
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
    };
    poll();
    return () => {
      stop = true;
    };
  }, [sessionId, success]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-wine px-5 text-center text-cream">
      <LogoMark className="h-12 w-12" />
      {status === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-gold" data-testid="payment-success-icon" />
          <h1 className="font-serif text-4xl font-light md:text-5xl" data-testid="payment-status-title">
            Shukriya — danke!
          </h1>
          <p className="max-w-sm text-base font-light text-rosy">
            Deine Zahlung ist bei uns eingegangen. Wir melden uns in Kürze persönlich
            wegen Versand und allen Details.
          </p>
        </>
      )}
      {status === "pending" && (
        <>
          <Loader2 className="h-14 w-14 animate-spin text-gold" />
          <h1 className="font-serif text-4xl font-light" data-testid="payment-status-title">
            Zahlung wird geprüft…
          </h1>
          <p className="max-w-sm text-base font-light text-rosy">Einen kleinen Moment bitte.</p>
        </>
      )}
      {(status === "cancel" || status === "failed" || status === "expired") && (
        <>
          <XCircle className="h-14 w-14 text-red-400" data-testid="payment-cancel-icon" />
          <h1 className="font-serif text-4xl font-light" data-testid="payment-status-title">
            Zahlung nicht abgeschlossen
          </h1>
          <p className="max-w-sm text-base font-light text-rosy">
            Kein Problem — schreib uns gerne per WhatsApp oder versuche es erneut.
          </p>
        </>
      )}
      <Link
        to="/"
        data-testid="payment-back-home"
        className="mt-4 rounded-full border border-gold px-8 py-3 text-sm font-medium uppercase tracking-widest text-gold transition-colors duration-300 hover:bg-gold hover:text-wine"
      >
        Zur Kollektion
      </Link>
    </div>
  );
}
